// Thème clair / sombre — source unique de vérité (cf. .claude/rules/scss-styles.md : « toggle
// via composable »). Le thème est une propriété du DOCUMENT, pas d'un composant : l'état est
// donc partagé au niveau module, sinon la topbar desktop et le shell mobile divergent.
//
// Un seul sélecteur est posé sur <html> : `data-theme="dark"`, écouté par ds/tokens.css (d'où tout
// le kit et le shell mobile dérivent leurs couleurs). La classe `.dark` — `darkModeSelector` de
// PrimeVue (cf. nuxt.config) — n'est volontairement jamais posée : le sombre n'est traité que sur
// les écrans DS. La poser assombrirait les composants PrimeVue (Select, Checkbox, InputText) sur
// des écrans legacy restés clairs, dont les fonds sont peints en Tailwind avec une variante `dark:`
// câblée sur `.p-dark` (cf. tailwind.config + theme.scss) — un sélecteur que rien ne pose.

const STORAGE_KEY = 'gd.theme';

// État module (et non par composant) : plusieurs consommateurs (Topbar, MobileApp) doivent lire
// et écrire le même thème.
const isDark = ref(false);
let initialized = false;

function applyToDocument (dark: boolean): void {
  if (!import.meta.client) { return; }
  const root = document.documentElement;
  if (dark) { root.setAttribute('data-theme', 'dark'); } else { root.removeAttribute('data-theme'); }
}

function readStoredPreference (): boolean | null {
  if (!import.meta.client) { return null; }
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === 'dark') { return true; }
    if (stored === 'light') { return false; }
  } catch {
    // localStorage indisponible (navigation privée, cookies bloqués) : on retombe sur le clair.
    // Le thème ne doit jamais casser l'app.
  }
  return null;
}

function persist (dark: boolean): void {
  if (!import.meta.client) { return; }
  try {
    window.localStorage.setItem(STORAGE_KEY, dark ? 'dark' : 'light');
  } catch {
    // Idem : préférence non mémorisée, mais la bascule reste effective pour la session.
  }
}

// Choix initial : préférence mémorisée, sinon clair. `prefers-color-scheme` n'est délibérément pas
// suivi — le sombre n'est traité que sur les écrans DS, il doit rester un choix explicite.
// Idempotent, et rejouable explicitement (tests).
function initDarkMode (): void {
  isDark.value = readStoredPreference() ?? false;
  applyToDocument(isDark.value);
  initialized = true;
}

function setDark (dark: boolean): void {
  isDark.value = dark;
  applyToDocument(dark);
  persist(dark);
}

export function useDarkMode (): {
  isDark: Readonly<Ref<boolean>>;
  toggle: () => void;
  setDark: (dark: boolean) => void;
  initDarkMode: () => void;
} {
  // Auto-init au premier usage : les points d'entrée sont multiples (layout desktop, shell
  // mobile) — exiger un appel explicite dans chacun garantirait qu'on l'oublie quelque part.
  if (!initialized && import.meta.client) { initDarkMode(); }

  return {
    isDark: readonly(isDark),
    toggle: () => setDark(!isDark.value),
    setDark,
    initDarkMode,
  };
}
